import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { ExternalLink, Mail, Search, Trash2 } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { adminApi, type Application, type ApplicationStatus } from '../../lib/adminApi';
import { refreshAdminOverview } from '../../lib/adminEvents';

const STATUSES: ApplicationStatus[] = ['new', 'reviewing', 'approved', 'declined'];

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const seconds = Math.floor((Date.now() - then) / 1000);
  if (seconds < 30) return 'just now';
  const table: Array<[number, string]> = [
    [31536000, 'year'], [2592000, 'month'], [604800, 'week'], [86400, 'day'], [3600, 'hour'], [60, 'minute'], [1, 'second'],
  ];
  for (const [secondsPer, unit] of table) {
    const value = Math.floor(seconds / secondsPer);
    if (value >= 1) return `${value} ${unit}${value === 1 ? '' : 's'} ago`;
  }
  return 'just now';
}

const APPLICATION_EMAILS = [
  {
    label: 'Schedule a call',
    subject: 'Next steps for your Sugarloaf Goldens application',
    body: (firstName: string) => `Hi ${firstName},\n\nThank you for sharing more about your family. We would love to schedule a call and learn a little more about the home you are preparing for a puppy.\n\nWhat days and times work well for you?\n\nWarmly,\nSugarloaf Goldens`,
  },
  {
    label: 'Approved family',
    subject: 'Your Sugarloaf Goldens application',
    body: (firstName: string) => `Hi ${firstName},\n\nWe are happy to let you know that your family has been approved for our waitlist. We will follow up with current litter timing and next steps.\n\nWarmly,\nSugarloaf Goldens`,
  },
  {
    label: 'Waitlist update',
    subject: 'Sugarloaf Goldens waitlist update',
    body: (firstName: string) => `Hi ${firstName},\n\nWe wanted to send a quick update about our upcoming litters and your place in the process.\n\nWarmly,\nSugarloaf Goldens`,
  },
];

export function Applications() {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState<ApplicationStatus | 'all'>('all');
  const [query, setQuery] = useState('');
  const [notice, setNotice] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const activeId = searchParams.get('id');
  const activeIdRef = useRef(activeId);
  const searchParamsRef = useRef(searchParams);
  activeIdRef.current = activeId;
  searchParamsRef.current = searchParams;

  const load = () => adminApi.getApplications().then(setApplications).catch(() => undefined).finally(() => setLoaded(true));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(''), 4200);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return applications.filter((application) => {
      if (filter !== 'all' && application.status !== filter) return false;
      if (!needle) return true;
      return `${application.applicantName} ${application.applicantEmail} ${Object.values(application.answers).join(' ')}`.toLowerCase().includes(needle);
    });
  }, [applications, filter, query]);
  const active = applications.find((application) => application.id === activeId) ?? null;

  const selectApplication = (id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('id', id);
    setSearchParams(next);
  };

  const changeStatus = async (id: string, status: ApplicationStatus) => {
    const updated = await adminApi.setApplicationStatus(id, status).catch(() => null);
    if (updated) {
      setApplications((current) => current.map((application) => (application.id === id ? updated : application)));
      refreshAdminOverview();
    }
  };

  const deleteApplication = async (application: Application) => {
    const confirmed = window.confirm(`Permanently delete ${application.applicantName}’s application?\n\nTheir email history will stay in Inbox. Any unsent reply text on this screen will be discarded. This cannot be undone.`);
    if (!confirmed) return false;
    try {
      await adminApi.deleteApplication(application.id);
      setApplications((current) => current.filter((item) => item.id !== application.id));
      if (activeIdRef.current === application.id) {
        const next = new URLSearchParams(searchParamsRef.current);
        next.delete('id');
        setSearchParams(next);
      }
      refreshAdminOverview();
      setNotice(`${application.applicantName}’s application was deleted.`);
      return true;
    } catch {
      setNotice('Could not delete the application. Please try again.');
      return false;
    }
  };

  return (
    <div className="cms-page cms-page-full">
      <div className="cms-page-head cms-page-head-row">
        <div>
          <h1>Applications</h1>
          <p>Every puppy application, from first submission to your final decision.</p>
        </div>
        <Link className="cms-btn cms-btn-ghost" to="/admin/form">Edit application form <ExternalLink size={14} /></Link>
      </div>

      <div className="cms-index-toolbar">
        <label className="cms-index-search">
          <Search size={15} aria-hidden="true" />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search names, email, or answers" aria-label="Search applications" />
        </label>
        <span className="cms-index-count">{visible.length} of {applications.length} applications</span>
      </div>

      <div className="cms-filterbar">
        {(['all', ...STATUSES] as const).map((status) => (
          <button key={status} type="button" className={`cms-chip ${filter === status ? 'active' : ''}`} aria-pressed={filter === status} onClick={() => setFilter(status)}>
            {status === 'all' ? 'All' : status}
            <b>{status === 'all' ? applications.length : applications.filter((a) => a.status === status).length}</b>
          </button>
        ))}
      </div>

      <div className="cms-inbox">
        <div className="cms-inbox-list">
          {!loaded && <p className="cms-empty">Loading…</p>}
          {loaded && !visible.length && <p className="cms-empty">No applications in this view yet.</p>}
          {visible.map((application) => (
            <button key={application.id} type="button" className={`cms-thread ${activeId === application.id ? 'active' : ''} ${application.status === 'new' ? 'unread' : ''}`} aria-pressed={activeId === application.id} onClick={() => selectApplication(application.id)}>
              <div className="cms-thread-row">
                <span className="cms-thread-from">{application.applicantName}</span>
                <time dateTime={application.submittedAt} title={new Date(application.submittedAt).toLocaleString()}>{timeAgo(application.submittedAt)}</time>
              </div>
              <span className="cms-thread-subject">{application.applicantEmail}</span>
              <span className={`cms-status cms-status-${application.status}`}>{application.status}</span>
            </button>
          ))}
        </div>

        <div className="cms-inbox-detail">
          {active ? <ApplicationDetail key={active.id} application={active} onStatus={changeStatus} onDelete={deleteApplication} /> : <p className="cms-empty cms-inbox-placeholder">Select an application to review it.</p>}
        </div>
      </div>
      {notice && <div className="cms-toast" role="status">{notice}</div>}
    </div>
  );
}

function ApplicationDetail({ application, onStatus, onDelete }: { application: Application; onStatus: (id: string, status: ApplicationStatus) => void; onDelete: (application: Application) => Promise<boolean> }) {
  const [body, setBody] = useState('');
  const [subject, setSubject] = useState('Re: Your Sugarloaf Goldens application');
  const [draftId, setDraftId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [note, setNote] = useState('');

  const remove = async () => {
    if (sending || deleting) return;
    setDeleting(true);
    const deleted = await onDelete(application);
    if (!deleted) setDeleting(false);
  };

  const reply = async (event: FormEvent) => {
    event.preventDefault();
    if (sending || deleting || !body.trim() || !subject.trim()) return;
    setSending(true);
    setNote('');
    let savedId = draftId;
    try {
      const payload = { threadId: application.id, toEmail: application.applicantEmail, subject: subject.trim(), text: body.trim(), html: null };
      const draft = savedId
        ? await adminApi.updateEmailDraft(savedId, payload)
        : await adminApi.createEmailDraft(payload);
      savedId = draft.id;
      setDraftId(draft.id);
      const result = await adminApi.sendEmailDraft(draft.id);
      setBody('');
      setDraftId(null);
      setNote(result.delivered ? 'Reply sent to the applicant and added to Sent.' : 'Reply remains saved in Drafts.');
    } catch {
      setNote(savedId ? 'Could not send. The message is safe in Inbox → Drafts.' : 'Could not save the message. Your text is still here—please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="cms-thread-view">
      <header className="cms-application-head">
        <div><h2>{application.applicantName}</h2><p>{application.applicantEmail} · applied {timeAgo(application.submittedAt)} · {new Date(application.submittedAt).toLocaleString()}</p></div>
        <button type="button" className="cms-btn cms-btn-danger" disabled={sending || deleting} onClick={() => void remove()}><Trash2 size={14} />{deleting ? 'Deleting…' : 'Delete application'}</button>
      </header>

      <div className="cms-statusbar">
        {STATUSES.map((status) => (
          <button key={status} type="button" className={`cms-chip ${application.status === status ? 'active' : ''}`} disabled={deleting} onClick={() => onStatus(application.id, status)}>{status}</button>
        ))}
      </div>

      <dl className="cms-answers">
        {Object.entries(application.answers).map(([key, value]) => (
          <div key={key}>
            <dt>{key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())}</dt>
            <dd>{typeof value === 'string' ? value : JSON.stringify(value)}</dd>
          </div>
        ))}
      </dl>

      <form className="cms-reply" onSubmit={reply}>
        <div className="cms-email-templates" aria-label="Application email templates">
          <span><Mail size={14} />Quick templates</span>
          {APPLICATION_EMAILS.map((template) => (
            <button key={template.label} type="button" className="cms-chip" disabled={sending || deleting} onClick={() => {
              setSubject(template.subject);
              setBody(template.body(application.applicantName.split(' ')[0] || 'there'));
            }}>{template.label}</button>
          ))}
        </div>
        <label className="cms-field">
          <span>Subject</span>
          <input value={subject} onChange={(event) => setSubject(event.target.value)} disabled={sending || deleting} required />
        </label>
        <label className="cms-field">
          <span>Email the applicant</span>
          <textarea rows={4} value={body} onChange={(event) => setBody(event.target.value)} placeholder={`Hi ${application.applicantName.split(' ')[0]}, thanks so much for applying…`} disabled={sending || deleting} required />
        </label>
        <div className="cms-reply-actions">
          {note && <span className="cms-reply-note">{note}</span>}
          <button type="submit" className="cms-btn cms-btn-primary" disabled={sending || deleting || !body.trim() || !subject.trim()}>{sending ? 'Saving & sending…' : 'Send email'}</button>
        </div>
      </form>
    </div>
  );
}
